import { useSuspenseQuery } from '@tanstack/react-query';
import { countsAsDiagnosis } from '@wodi/domain';
import type { WorkspaceView } from '@wodi/shared';
import type { ComponentType } from 'react';

import { sessionQuery } from '../api/session.ts';
import { ConsultationDocumentsPanel } from '../documents/documents-panel.tsx';
import { documentPermissions } from '../documents/permissions.ts';
import { PrescribingPanel } from '../prescribing/prescribing-panel.tsx';

// Panel slots of the consultation workspace's right column (docs/plans/phase1-consultation.md
// §11: this file is the workspace's slot list, owned by the consultation slice). The
// prescribing slice (C3) replaces `PrescribingPanel` with its own panel; clinical documents
// (C4: sick sheets, referral letters, uploads) add theirs the same way.

export interface WorkspacePanelProps {
  view: WorkspaceView;
  /** The signed-in clinician's consultation in progress, or null when only reading. */
  consultationId: string | null;
}

/** The prescribing slice's panel (C3), for this visit and the clinician's consultation. */
function Prescribing({ view, consultationId }: WorkspacePanelProps) {
  return (
    <PrescribingPanel
      visitId={view.visit.id}
      patientId={view.visit.patientId}
      consultationId={consultationId}
    />
  );
}

/** Clinical documents (C4): sick sheets, referral letters and uploaded documents. */
function Documents({ view, consultationId }: WorkspacePanelProps) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  return (
    <ConsultationDocumentsPanel
      visitId={view.visit.id}
      patientId={view.visit.patientId}
      consultationId={consultationId}
      visitOpen={view.visit.status === 'open'}
      visitStartedAt={view.visit.startedAt}
      diagnoses={view.diagnoses
        .filter(countsAsDiagnosis)
        .map((d) => ({ id: d.id, words: d.coding?.display ?? d.clinicianText ?? '' }))}
      may={documentPermissions(session)}
    />
  );
}

export const WORKSPACE_PANELS: {
  prescribing: ComponentType<WorkspacePanelProps>;
  documents: ComponentType<WorkspacePanelProps> | null;
} = {
  prescribing: Prescribing,
  documents: Documents,
};
